import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../api";
import { LOCAL_STORAGE_KEYS } from "../../../utils/constants";
import {
  wishlistSlice,
  getWishlistSelector,
} from "../../../store/wishlistSlice";

/**
 * @desc Custom hook for handling wishlist.
 *  - Wishlist ID is stored in localstorage
 *  - Dispatch to edit wishlist state
 */
export const useHandleWishlist = ({ brand, cname, isLive = false }) => {
  // CONSTANTS ----------------------------------------------------------------

  const dispatch = useDispatch();
  const wishlist = useSelector(getWishlistSelector);
  const wkey = LOCAL_STORAGE_KEYS[brand];
  let localstorageWID = window.localStorage.getItem(wkey);
  localstorageWID =
    localstorageWID === "undefined" ? undefined : localstorageWID;

  // COMPONENT STATE ----------------------------------------------------------

  const [wid, setWid] = useState(localstorageWID);

  // METHODS ------------------------------------------------------------------

  /**
   * @desc Creates a new wishlist and updates local storage.
   */
  const wishlistErrorHandler = useCallback(
    (key) => {
      if (!cname || !key) return;
      API.Wishlist.Create({ params: { cname } }).then((res) => {
        if (res && typeof res === typeof {} && res._id) {
          window.localStorage.setItem(key, res._id);
          setWid(res._id);
        }
      });
    },
    [cname]
  );

  // METHODS ------------------------------------------------------------------

  /**
   * @desc Set wishlist.
   */
  const setWishlist = useCallback(
    (wishlist) => {
      if (wishlist && typeof wishlist === typeof {}) {
        dispatch(wishlistSlice.actions.setWishlistStore(wishlist));
      }
    },
    [dispatch]
  );

  /**
   * @desc Show wishlist.
   */
  const setShowWishlist = useCallback(
    (show) => {
      dispatch(wishlistSlice.actions.setShowWishlist(show));
    },
    [dispatch]
  );

  // SIDE EFFECTS -------------------------------------------------------------

  /**
   * @desc Only once:
   *  - Get wishlist if it exists
   *  - OR create a new one
   */
  useEffect(() => {
    const main = async () => {
      if (isLive && brand && cname) {
        if (wkey && wid) {
          try {
            const res = await API.Wishlist.Get({ params: { id: wid } });
            setWishlist(res);
          } catch (err) {
            wishlistErrorHandler(wkey);
          }
        } else {
          wishlistErrorHandler(wkey);
        }
      }
    };

    main();
  }, [cname, brand, isLive, wishlistErrorHandler, wkey, wid, setWishlist]);

  // HOOK ---------------------------------------------------------------------

  return {
    wid,
    wishlist,
    setWishlist,
    setShowWishlist,
  };
};
